import { l } from '@ds/index'

import { useAppSelector } from 'reduxTyped'
import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { ProductSlug } from 'common/billing/constants/ProductSlug'
import { AddOnSlug } from 'common/billing/entities/addOns/interfaces'
import { getActiveContacts } from 'common/billing/entities/billingVersions/selectors'
import { getCurrency } from 'common/billing/entities/pro/utils/convertPrice'
import {
  CurrentSubscription,
  useCurrentSubscription,
} from 'common/billing/hooks/useCurrentSubscription'
import { Ability } from 'common/core/constants/Ability'
import { useAbility } from 'common/core/hooks/useAbility'

import { createPriceBreakdownArray } from '../SubscriptionSuccessModal/features'

export const formatMonthlyPrice = (amount: number, currency: BillingCurrency) =>
  l.makeTranslate('{price}/mo', { price: l.currency(amount, currency, 0) })

interface CurrentPlanPriceBreakdownParams {
  subscription: Nullable<Pick<CurrentSubscription, 'plans' | 'addOns' | 'livechatSeatsPrice'>>
  activeContacts: number
  inboxSeatsLimit: number
  currency: BillingCurrency
}

const getCurrentPlanPriceBreakdown = ({
  subscription,
  activeContacts,
  inboxSeatsLimit,
  currency,
}: CurrentPlanPriceBreakdownParams) => {
  const { plans = [], addOns = [], livechatSeatsPrice } = subscription || {}
  const automationPlan = plans.find((plan) => plan.slug === ProductSlug.AUTOMATION)
  const aiAddOn = addOns.find((addOn) => addOn.slug === AddOnSlug.AI)
  const inboxPlan = plans.find((plan) => plan.slug === ProductSlug.INBOX)

  return createPriceBreakdownArray({
    storedContacts: {
      value: formatMonthlyPrice(
        automationPlan?.price.amount ?? 0,
        automationPlan?.price.currency ?? currency,
      ),
      tooltip: l.makeTranslate(
        "Everyone who's messaged you, opted in, or been imported, kept in your Contacts tab. They're priced in bands, each with a fixed monthly price.",
      ),
    },
    activeContacts: {
      value: l.makeTranslate('Not billed'),
      note: l.makeTranslate("You're at {activeContacts} this month", { activeContacts }),
      tooltip: l.makeTranslate(
        "People who’ve actually interacted with you (messaged you or triggered one of your automations) during the billing period. Your current plan doesn't bill for this.",
      ),
    },
    ai: {
      value: aiAddOn
        ? formatMonthlyPrice(aiAddOn.price.amount, aiAddOn.price.currency)
        : l.makeTranslate('Costs extra'),
    },
    inbox: {
      value:
        inboxPlan && livechatSeatsPrice
          ? formatMonthlyPrice(livechatSeatsPrice.amount, livechatSeatsPrice.currency)
          : l.makeTranslate('Costs extra'),
      tooltip: l.makeTranslate(
        'Manage inbound leads from messaging apps in one centralized view to increase response times and drive sales. Includes {limit, plural, one {# Inbox seat} other {# Inbox seats}}.',
        { limit: inboxSeatsLimit },
      ),
    },
  })
}

export const useCurrentPlanPriceBreakdown = () => {
  const activeContacts = useAppSelector(getActiveContacts)
  const inboxSeats = useAbility(Ability.LIVECHAT_SEATS)
  const currentSubscription = useCurrentSubscription()
  const currency = currentSubscription?.total.currency ?? getCurrency()

  return {
    features: getCurrentPlanPriceBreakdown({
      subscription: currentSubscription,
      activeContacts,
      inboxSeatsLimit: inboxSeats.limit,
      currency,
    }),
    total: currentSubscription?.total ?? { amount: 0, currency },
  }
}
